import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  YesNoQuestion,
  QuestionAnswer,
  QuestionInterface,
  GenderQuestion,
  YesNoGenderQuestion
} from './questions/QuestionTypes';

// Normalization utility: capitalize words, keep abbreviations in () or after separators uppercase
const normalizePartyName = (raw: string): string => {
  if (!raw) return '';
  let s = raw.trim();
  // Split out parentheses abbreviation
  const parenMatch = s.match(/^(.*?)(\s*\(([^)]+)\)\s*)$/);
  let main = s;
  let abbr = '';
  if (parenMatch) {
    main = parenMatch[1];
    abbr = parenMatch[3].toUpperCase();
  }
  // Handle separators like '-' or '=' for trailing abbreviation
  let trailingAbbr = '';
  let sep = '';
  const sepMatch = main.match(/^(.*?)(\s*[-=]\s*)([A-Za-z]{2,})\s*$/);
  if (sepMatch && !abbr) {
    main = sepMatch[1];
    sep = sepMatch[2].trim();
    trailingAbbr = sepMatch[3].toUpperCase();
  }
  // Capitalize each word in main (first letter upper, rest lower)
  const cap = (w: string) => w ? (w[0].toUpperCase() + w.slice(1).toLowerCase()) : w;
  const normalizedMain = main.split(/\s+/).map(cap).join(' ').trim();
  // Reconstruct
  let result = normalizedMain;
  if (abbr) result += ` (${abbr})`;
  else if (trailingAbbr) result += ` ${sep} ${trailingAbbr}`;
  return result.replace(/\s{2,}/g, ' ').trim();
};

// Update political parties list (raw) and sort after normalization
const POLITICAL_PARTIES_RAW = [
  'people party - pp',
  'Anyamata Atsikana Azimayi - AAA',
  'MALAWI CONGRES PAARTY - MCP',
  'PEOPLES TRANFORMATION PARTY - PETRA',
  'unITED TRANSFORMATION PARTY - UTM',
  'DEMOCRATIC PROGRSSIVE PARTY - DPP',
  'UNITED DEMOCRATIC FRONT - UDF',
  'National Development Party - NDP',
  "Peoples's Development Party - PDP",
  'Patriotic Citizens Party - PCP',
  'Alliance for Democracy - AFORD',
  'Odya Zake',
  'Independent',

];
const POLITICAL_PARTIES = POLITICAL_PARTIES_RAW
  .map(normalizePartyName)
  .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

interface PartyRepresentative {
  id: string;
  partyName: string;
  // Gender-specific counts per party
  maleRepresentatives: number;
  femaleRepresentatives: number;
}

// We need to create new question types for Number inputs
interface NumberQuestionProps {
  id: string;
  question: string;
  value: QuestionAnswer;
  onChange: (value: QuestionAnswer) => void;
  required?: boolean;
  error?: string;
}

const NumberQuestionComponent: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="number"
      className="w-32 input"
      min="0"
      value={value.answer || ''}
      onChange={(e) => onChange({ ...value, answer: e.target.value })}
    />
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

interface Question2Data {
  [questionId: string]: QuestionAnswer;
}

interface Question2Props {
  data: Question2Data;
  onChange: (data: Question2Data) => void;
  errors?: Record<string, string>;
  timeline?: string;
  questionNumber?: number;
  questionTitle?: string;
  questionDescription?: string;
  currentSection?: number;
  totalSections?: number;
}

// Timeline-based questions for Question 2 (Personnel Present)
const TIMELINE_QUESTIONS: Record<string, QuestionInterface[]> = {
  morning: [
    { id: '2a', question: 'Total number of Electoral Commission Officials present', type: 'gender', required: true },
    { id: '2b', question: 'Total number of security personnel present', type: 'gender', required: true },
    // 2c now collects simple Yes/No (gender moved to per-party entries only)
    { id: '2c', question: 'Are there Official Political Party Representatives present?', type: 'yes_no', required: true },
    { id: '2d', question: 'Are the Political Party Representatives allowed to perform their duties without any interference/hindrance?', type: 'yes_no', required: true },
  ],
  afternoon: [
    { id: '11a', question: 'Total number of Electoral Commission Officials present', type: 'gender', required: true },
    { id: '11b', question: 'Total number of security personnel present', type: 'gender', required: true },
    { id: '11c', question: 'Are there Official Political Party Representatives present?', type: 'yes_no', required: true },
    { id: '11d', question: 'Are the Political Party Representatives allowed to perform their duties without any interference/hindrance?', type: 'yes_no', required: true },
  ],
  vote_count: [
    { id: '22b', question: 'Number of unused ballot papers at the Polling Centre', type: 'number', required: true },
    { id: '22c', question: 'Number of spoilt ballot papers at the Polling Centre', type: 'number', required: true },
    { id: '22d', question: 'Number of null and void votes at the Polling Centre', type: 'number', required: true },
    { id: '22e', question: 'Were party representatives present at vote counting?', type: 'yes_no', required: true },
    { id: '22f', question: 'Were observers present and able to perform their duty at vote counting?', type: 'yes_no', required: true }
  ]
};

const QUESTION_COMPONENTS = {
  'yes_no': YesNoQuestion,
  'number': NumberQuestionComponent,
  'gender': GenderQuestion,
  'yes_no_gender': YesNoGenderQuestion,
};

const Step3Question2: React.FC<Question2Props> = ({
  data,
  onChange,
  errors = {},
  timeline: selectedTimeline,
  questionNumber = 2,
  questionTitle = "Personnel Present",
  questionDescription = "This section covers personnel present at the polling station including electoral commission officials, security personnel, and political party representatives",
  currentSection = 2,
  totalSections = 11
}) => {
  const updateQuestion = (questionId: string, value: QuestionAnswer) => {
    onChange({
      ...data,
      [questionId]: value
    });
  };

  // Get party representatives from QuestionAnswer format
  const getPartyRepresentatives = (): PartyRepresentative[] => {
    const partyKeyMorning = '2c_parties';
    const partyKeyAfternoon = '11c_parties';
    const partyKeyEvening = '22c_parties';
    const partyKeyVoteCount = '22e_parties';
    const partyRepsData = data[partyKeyMorning]?.details || data[partyKeyAfternoon]?.details || data[partyKeyEvening]?.details || data[partyKeyVoteCount]?.details;
    if (!partyRepsData) return [];
    try {
      const arr = JSON.parse(partyRepsData);
      return arr.map((p: any) => ({
        id: p.id,
        partyName: normalizePartyName(p.partyName || ''),
        maleRepresentatives: Number(p.maleRepresentatives || 0),
        femaleRepresentatives: Number(p.femaleRepresentatives || 0),
      }));
    } catch {
      return [];
    }
  };

  // Save party representatives to QuestionAnswer format
  const savePartyRepresentatives = (parties: PartyRepresentative[]) => {
    // Determine which key to use based on which question triggered it
    const isVoteCount = data['22e']?.answer === 'Yes';
    const isEvening = data['22c']?.answer === 'Yes';
    const isAfternoon = data['11c']?.answer === 'Yes';
    const partyKey = isVoteCount ? '22e_parties' : isEvening ? '22c_parties' : isAfternoon ? '11c_parties' : '2c_parties';

    // Store normalized
    const payload = parties.map(p => ({
      id: p.id,
      partyName: normalizePartyName(p.partyName),
      maleRepresentatives: Number(p.maleRepresentatives || 0),
      femaleRepresentatives: Number(p.femaleRepresentatives || 0),
    }));
    updateQuestion(partyKey, {
      answer: 'data',
      details: JSON.stringify(payload)
    });
  };

  // Party Representatives Management
  const addParty = () => {
    const newParty: PartyRepresentative = {
      id: `party_${Date.now()}`,
      partyName: '',
      maleRepresentatives: 0,
      femaleRepresentatives: 0,
    };
    const currentParties = getPartyRepresentatives();
    savePartyRepresentatives([...currentParties, newParty]);
  };

  const removeParty = (id: string) => {
    const currentParties = getPartyRepresentatives();
    savePartyRepresentatives(currentParties.filter(party => party.id !== id));
  };

  const updateParty = (id: string, field: keyof PartyRepresentative, value: string | number) => {
    const currentParties = getPartyRepresentatives();
    savePartyRepresentatives(currentParties.map(party =>
      party.id === id ? { ...party, [field]: typeof value === 'string' ? value : Number(value) } : party
    ));
  };

  const getTotalRepresentatives = () => {
    return getPartyRepresentatives().reduce((total, party) => total + (party.maleRepresentatives || 0) + (party.femaleRepresentatives || 0), 0);
  };

  const getAvailableParties = (currentPartyName: string) => {
    const usedParties = getPartyRepresentatives()
      .filter(party => party.partyName && normalizePartyName(party.partyName) !== normalizePartyName(currentPartyName))
      .map(party => normalizePartyName(party.partyName));
    return POLITICAL_PARTIES.filter(p => !usedParties.includes(p));
  };

  const partyRepresentatives = getPartyRepresentatives();

  // Check if party representatives section should be shown
  const shouldShowPartyReps = data['2c']?.answer === 'Yes' || data['11c']?.answer === 'Yes' || data['22c']?.answer === 'Yes' || data['22e']?.answer === 'Yes';

  // Get questions for the selected timeline with conditional filtering
  const getQuestionsForTimeline = () => {
    if (!selectedTimeline || !TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS]) {
      return [];
    }

    const allQuestions = TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS];

    // Filter out conditional questions that shouldn't be shown
    return allQuestions.filter(question => {
      if (!question.conditional) return true;

      const dependentAnswer = data?.[question.conditional.dependsOn]?.answer;
      return dependentAnswer === question.conditional.showWhen;
    });
  };

  const questions = getQuestionsForTimeline();

  const getCompletionStatus = () => {
    const completed = questions.filter(q => data?.[q.id]?.answer).length;
    return { completed, total: questions.length };
  };

  const { completed, total } = getCompletionStatus();

  return (
    <div className="space-y-6">
      {/* Section Header with Step Number and Progress */}
      <div className="p-4 border border-blue-100 rounded-lg bg-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Question {questionNumber}: {questionTitle}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {questionDescription}
            </p>
          </div>
          <div className="text-right">
            <span className="px-3 py-1 text-sm font-medium text-blue-800 bg-blue-100 rounded-full">
              Section {currentSection}/{totalSections}
            </span>
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 rounded-full"
            style={{ width: `${(currentSection / totalSections) * 100}%` }}
          ></div>
        </div>
      </div>

      <div>
        <p className="mb-4 text-gray-600">
          Timeline: {selectedTimeline ? selectedTimeline.charAt(0).toUpperCase() + selectedTimeline.slice(1).replace('_', ' ') : 'No timeline selected'}
        </p>
        {!selectedTimeline && (
          <div className="p-4 mb-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <p className="text-yellow-800">Please select a timeline in Step 1 to see relevant questions for Question {questionNumber}.</p>
          </div>
        )}

        {/* Progress indicator */}
        <div className="p-3 mb-6 border border-green-200 rounded-lg bg-green-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-green-900">Question {questionNumber} Progress</span>
            <span className="text-sm text-green-800">{completed}/{total} completed</span>
          </div>
          <div className="h-2 mt-2 bg-green-200 rounded-full">
            <div
              className="h-2 transition-all duration-300 bg-green-600 rounded-full"
              style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.length > 0 ? questions.map((question) => {
          const QuestionComponent = QUESTION_COMPONENTS[question.type as keyof typeof QUESTION_COMPONENTS];
          const questionValue = data?.[question.id] || {};
          const questionError = errors?.[question.id];

          return (
            <div key={question.id} className="space-y-4">
              <div className="p-4 transition-colors bg-white border border-gray-200 rounded-lg hover:border-green-300">
                <QuestionComponent
                  id={question.id}
                  question={`${question.id}: ${question.question}`}
                  value={questionValue}
                  onChange={(value) => updateQuestion(question.id, value)}
                  required={question.required}
                  error={questionError}
                />
              </div>

              {/* Show Party Representatives section if question 2c, 11c, 22c, or 22e is answered Yes */}
              {(question.id === '2c' || question.id === '11c' || question.id === '22c' || question.id === '22e') && shouldShowPartyReps && (
                <div className="p-6 ml-8 border border-blue-200 rounded-lg bg-blue-50">
                  <div className="space-y-6">
                    <div>
                      <h3 className="mb-2 text-lg font-semibold text-gray-900">
                        {question.id === '22e' ? 'Party Representatives at Vote Counting' : 'Political Party Representatives Details'}
                      </h3>
                      <p className="mb-4 text-gray-600">
                        {question.id === '22e'
                          ? 'Add the political parties present during vote counting and count their representatives.'
                          : 'Add the political parties present and count their representatives.'
                        }
                      </p>
                    </div>

                    {/* Add Party Button */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={addParty}
                        className="flex items-center space-x-2 btn btn-primary"
                        type="button"
                      >
                        <PlusIcon className="w-5 h-5" />
                        <span>Add Political Party</span>
                      </button>

                      {partyRepresentatives.length > 0 && (
                        <div className="text-sm text-gray-600">
                          Total Representatives: <span className="font-semibold text-blue-600">{getTotalRepresentatives()}</span>
                        </div>
                      )}
                    </div>

                    {/* Party Representatives List */}
                    {partyRepresentatives.length > 0 ? (
                      <div className="space-y-4">
                        {partyRepresentatives.map((party, index) => (
                          <div key={party.id} className="p-4 bg-white border border-gray-200 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900">Party {index + 1}</h4>
                              <button
                                onClick={() => removeParty(party.id)}
                                className="p-1 text-red-600 hover:text-red-800"
                                title="Remove Party"
                                type="button"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              {/* Party Name */}
                              <div>
                                <label className="label">
                                  Political Party <span className="text-red-500">*</span>
                                </label>
                                <select
                                  className="input"
                                  value={party.partyName}
                                  onChange={(e) => updateParty(party.id, 'partyName', e.target.value)}
                                >
                                  <option value="">Select Political Party</option>
                                  {getAvailableParties(party.partyName).map((partyName) => (
                                    <option key={partyName} value={partyName}>
                                      {partyName}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Number of Representatives */}
                              <div>
                                <label className="label">
                                  Number of Representatives <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-4">
                                  {/* Male Representatives */}
                                  <div>
                                    <label className="label">Male </label>
                                    <input
                                      type="number"
                                      className="w-full input"
                                      value={party.maleRepresentatives}
                                      min="0"
                                      onChange={(e) => {
                                        const value = Math.max(0, parseInt(e.target.value) || 0);
                                        updateParty(party.id, 'maleRepresentatives', value);
                                      }}
                                    />
                                  </div>

                                  {/* Female Representatives */}
                                  <div>
                                    <label className="label">Female </label>
                                    <input
                                      type="number"
                                      className="w-full input"
                                      value={party.femaleRepresentatives}
                                      min="0"
                                      onChange={(e) => {
                                        const value = Math.max(0, parseInt(e.target.value) || 0);
                                        updateParty(party.id, 'femaleRepresentatives', value);
                                      }}
                                    />
                                  </div>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">Enter counts for each gender</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-6 text-center border-2 border-gray-300 border-dashed rounded-lg bg-gray-50">
                        <p className="mb-2 text-gray-600">No political parties added yet</p>
                        <p className="text-sm text-gray-500">Click "Add Political Party" to get started</p>
                      </div>
                    )}

                    {/* Summary */}
                    {partyRepresentatives.length > 0 && (
                      <div className="p-4 bg-blue-100 border border-blue-300 rounded-lg">
                        <h4 className="mb-2 font-medium text-blue-900">Summary</h4>
                        <div className="text-sm text-blue-800">
                          <p>Political Parties: {partyRepresentatives.filter(p => p.partyName).length}</p>
                          <p>Total Representatives: {getTotalRepresentatives()}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }) : (
          <div className="py-8 text-center text-gray-500">
            {selectedTimeline ? 'No questions available for this timeline.' : 'Select a timeline to view questions.'}
          </div>
        )}
      </div>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <h4 className="mb-2 font-medium text-red-900">Please complete the following required questions:</h4>
          <ul className="space-y-1 text-sm text-red-800">
            {Object.entries(errors).map(([questionId, error]) => (
              <li key={questionId}>• {questionId}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Period Summary */}
      <div className="p-4 rounded-lg bg-gray-50">
        <h4 className="mb-2 font-medium text-gray-900">Summary</h4>
        <p className="text-sm text-gray-700">
          This section covers personnel present at the polling station including electoral commission
          officials, security personnel, and political party representatives.
        </p>
        <div className="mt-2 text-sm text-gray-600">
          <p>Completion: {completed}/{total} questions ({total > 0 ? Math.round((completed / total) * 100) : 0}%)</p>
          {shouldShowPartyReps && (
            <p>Political Party Representatives: {partyRepresentatives.filter(p => p.partyName).length} parties, {getTotalRepresentatives()} total representatives</p>
          )}
        </div>
      </div>

      {/* Footer with Question Counter and Progress */}
      <div className="p-4 mt-8 border border-gray-100 rounded-lg bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <span className="font-medium">{total}</span> questions in this section
          </div>
          <div className="flex items-center text-sm">
            <span className="mr-2 font-medium text-blue-600">Question {questionNumber} of {totalSections}</span>
            <div className="flex space-x-1">
              {Array.from({ length: totalSections }, (_, i) => (
                <div
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full ${i < currentSection ? 'bg-blue-600' : 'bg-gray-300'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3Question2;